import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Zap, MoreHorizontal, Link, Target, Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface RelayNodeData {
  deviceId: string;
  deviceName: string;
  assetName?: string;
  nvrName?: string;
  nvrId?: string;
  associatedCameraId?: string;
  associatedCameraIds?: string[];
  hoveredCameraId?: string | null;
  onLinkToAllCameras?: (deviceId: string) => void;
  onEnterLinkingMode?: (deviceId: string, deviceType: 'relay') => void;
  onRemoveFromZone?: (deviceId: string, deviceType: 'relay') => void;
  onMouseEnter?: (deviceId: string) => void;
  onMouseLeave?: () => void;
}

interface RelayNodeProps {
  id: string;
  data: RelayNodeData;
  selected?: boolean;
}

export const RelayNode: React.FC<RelayNodeProps> = memo(({ data, selected }) => {
  const associatedCameraIds = data.associatedCameraIds || [data.associatedCameraId].filter(Boolean);
  const isConnectedToHoveredCamera = data.hoveredCameraId && associatedCameraIds.includes(data.hoveredCameraId);
  const isThisRelayHovered = data.hoveredCameraId === data.deviceId;
  const isDimmed = data.hoveredCameraId && !associatedCameraIds.includes(data.hoveredCameraId) && !isThisRelayHovered && associatedCameraIds.length > 0;
  
  
  const handleLinkToAllCameras = () => {
    if (data.onLinkToAllCameras) {
      data.onLinkToAllCameras(data.deviceId);
    }
  };

  const handleEnterLinkingMode = () => {
    if (data.onEnterLinkingMode) {
      data.onEnterLinkingMode(data.deviceId, 'relay');
    }
  };

  const handleRemoveFromZone = () => {
    if (data.onRemoveFromZone) {
      data.onRemoveFromZone(data.deviceId, 'relay');
    }
  };

  const handleViewDevice = () => {
    if (data.nvrId) {
      window.location.href = `/devices/${data.nvrId}/edit`;
    }
  };
  
  return (
    <div className={`rounded-lg shadow-lg p-2 min-w-[169px] transition-all duration-200 ${
      selected ? 'ring-2 ring-blue-400 ring-opacity-75' : ''
    } ${
      isConnectedToHoveredCamera ? 'border-2 border-yellow-400 shadow-lg shadow-yellow-400/20' : 'border border-yellow-500'
    } ${
      isDimmed ? 'opacity-40' : 'opacity-100'
    }`}
    style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
    onMouseEnter={() => data.onMouseEnter?.(data.deviceId)}
    onMouseLeave={() => data.onMouseLeave?.()}>
      {/* Relay Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-yellow-400" />
          <div className="flex-1 min-w-0">
            {data.nvrName && (
              <div 
                className="text-sm font-semibold text-white truncate max-w-[80px]"
                title={data.nvrName}
              >
                {data.nvrName}
              </div>
            )}
            <div 
              className="text-xs text-gray-400 truncate max-w-[80px]"
              title={data.deviceName}
            >
              {data.deviceName}
            </div>
          </div>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-600"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem 
              onClick={handleViewDevice} 
              className="cursor-pointer"
              disabled={!data.nvrId}
            >
              <Eye className="h-4 w-4 mr-2" />
              View Device
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLinkToAllCameras} className="cursor-pointer">
              <Link className="h-4 w-4 mr-2" />
              Link to all cameras
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleEnterLinkingMode} className="cursor-pointer">
              <Target className="h-4 w-4 mr-2" />
              Linking mode
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleRemoveFromZone} 
              className="cursor-pointer text-red-400 hover:text-red-300 focus:text-red-300"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove from zone
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* React Flow Handles - Hidden but functional */}
      <Handle
        id="top"
        type="target"
        position={Position.Top}
        isConnectable={true}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
      <Handle
        id="bottom"
        type="source"
        position={Position.Bottom}
        isConnectable={true}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
    </div>
  );
});

RelayNode.displayName = 'RelayNode';